import { Box, FlexBox, Typography } from "@vp/swan";
import AsLowAsPricePerPiece from "../legacy/AsLowAsPricePerPiece";
import LegacyRangePrice from "../legacy/LegacyRangePrice";
import PlusOrMinus from "../legacy/PlusOrMinus";
import PriceForQtyUnits from "../legacy/PriceForQtyUnits";
import PricePerUnit from "../legacy/PricePerUnit";
import QtyFromPrice from "../legacy/QtyFromPrice";
import QtyStartingAtPrice from "../legacy/QtyStartingAtPrice";
import RawPrice from "../legacy/RawPrice";
import VatMessage from "../legacy/VatMessage";
import React, { JSX } from "react";

import { AlignVAT, DifferentialPrice, DifferentialPriceForQuantity, FromPrice, MinimumOrderableQuantity, PriceDisplayMode, PriceInfo, QuantityForPrice, RangePrice } from "@vp/ep-pricing";
import { SHOW_ELEV8_WITH_TOTALPRICE_ON_CONSUMER } from "../../constants/featureFlags";
import { useFeatureToggle } from "../../hooks/useFeatureToggle";
import { LocalizationsProvider } from "../../hooks/useLocalizations";
import { getLocalizationsFor } from "../../localization/getLocalizations";
import { Locale } from "../../types/locales";
import { PageId } from "../../types/pageId";
import { Price, PricePresentationType, PriceProperties, PricesTypesBasedOnPricePresentation, PriceType } from "../../types/prices";
import {
  getAsLowAsPerPiecePrice,
  getCombinedPriceConfig,
  getDetailedPriceConfig,
  getDifferentialPriceConfig,
  getDifferentialPriceForQuantityConfig,
  getFromPriceConfig,
  getPlusMinusPrice,
  getPrice,
  getPriceForQuantityUnits,
  getPricePerUnit,
  getPriceWithQtyAndUnit,
  getQuantityForPrice,
  getQuantityFromPrice,
  getQuantityPrice,
  getRangedPrice,
  getRangePriceConfig,
  getRawAndUnitPrice,
} from "../../util/price-transform";
import { SetVersionOnWindow } from "../../util/version-logger";
import CSRWrapper from "../CSRWrapper";
import PriceErrorBoundary from "../PriceErrorBoundary";
import PriceLoading from "../PriceLoading";
import Savings from "../Savings";
import { SetupFee } from "../SetupFee";

// Accept pricePresentationType, prices and priceProperties and display price according to the pricePresentationType
interface BasePricePresentationProps {
  presentationType: PricePresentationType
  prices?: Partial<Record<PriceType, Price | number>>
  priceProperties: PriceProperties
  placeHolderText?: string
  className?: string
  pageId?: PageId
}

type PriceStructure = {
  unitListPrice?: Price
  unitDiscountedPrice?: Price
  setupFeeListPrice?: Price
  setupFeeDiscountedPrice?: Price
  unitListPriceForMoq?: Price
  listPriceForMoq?: Price
  baseListPrice?: Price
  baseDiscountedPrice?: Price
};
// Type that matches the specific presentation type
type PricePresentationPropDecider<T extends PricePresentationType> = T extends
  | PricePresentationType.PlusOrMinus
  ? BasePricePresentationProps & {
      presentationType: T
      prices: Record<PricesTypesBasedOnPricePresentation<T>, number>
    }
  : T extends PricePresentationType.SetupFee
  ? BasePricePresentationProps & {
      presentationType: T
      prices?: Record<PricesTypesBasedOnPricePresentation<T>, Price>
    }
  : BasePricePresentationProps & {
      presentationType: T
      prices: Record<PricesTypesBasedOnPricePresentation<T>, Price>
    };

// Consolidated type for PricePresentationProps
type PricePresentationProps =
  | PricePresentationPropDecider<PricePresentationType.PricePerUnit>
  | (PricePresentationPropDecider<PricePresentationType.QtyStartingAtPrice> & {
      priceProperties: { quantity: number }
    })
  | PricePresentationPropDecider<PricePresentationType.RangePrice>
  | (PricePresentationPropDecider<PricePresentationType.UnitRangePrice> & {
      prices: { setupFeeListPrice?: Price; setupFeeDiscountedPrice?: Price }
      pageId?: PageId
    })
  | (PricePresentationPropDecider<PricePresentationType.UnitRangePriceForTile> & {
      prices: { setupFeeListPrice?: Price; setupFeeDiscountedPrice?: Price }
      pageId?: PageId
    })
  | PricePresentationPropDecider<PricePresentationType.RawPrice>
  | PricePresentationPropDecider<PricePresentationType.RawAndPricePerUnit>
  | (PricePresentationPropDecider<PricePresentationType.QtyFromPrice> & {
      priceProperties: { quantity: number }
    })
  | (PricePresentationPropDecider<PricePresentationType.PriceForQtyUnits> & {
      priceProperties: { quantity: number }
    })
  | (PricePresentationPropDecider<PricePresentationType.PriceWithQtyAndUnit> & {
      prices: { unitListPriceForMoq: Price, listPriceForMoq: Price }
    } & {
      priceProperties: { quantity: number }
    })
  | PricePresentationPropDecider<PricePresentationType.AsLowAsPricePerPiece>
  | PricePresentationPropDecider<PricePresentationType.PlusOrMinus>
  | (PricePresentationPropDecider<PricePresentationType.TotalPrice> & {
    priceProperties: { quantity: number }
    prices: PriceStructure
  })
  | (PricePresentationPropDecider<PricePresentationType.PriceInfo> & {
      priceProperties: { quantity: number }
      prices: PriceStructure
    })
  | (PricePresentationPropDecider<PricePresentationType.UnitPriceInfo> & {
      priceProperties: { quantity: number }
      prices: PriceStructure
    })
  | (PricePresentationPropDecider<PricePresentationType.DetailedPriceInfo> & {
      priceProperties: { quantity: number, moq?: number }
      prices: PriceStructure
    })
  | (PricePresentationPropDecider<PricePresentationType.FromPrice> & {
      priceProperties: { quantity: number }
      prices: { unitListPrice?: Price; unitDiscountedPrice?: Price }
    })
  | (PricePresentationPropDecider<PricePresentationType.UnitFromPrice> & {
      priceProperties: { quantity: number }
      prices: { unitListPrice?: Price; unitDiscountedPrice?: Price }
    })
  | PricePresentationPropDecider<PricePresentationType.DifferentialPrice>
  | (PricePresentationPropDecider<PricePresentationType.DifferentialPriceForQuantity> & {
      priceProperties: { quantity: number }
    })
  | (PricePresentationPropDecider<PricePresentationType.SetupFee> & {
      prices?: { setupFeeListPrice?: Price; setupFeeDiscountedPrice?: Price }
    })
  | (PricePresentationPropDecider<PricePresentationType.QtyForPrice> & { priceProperties: { quantity: number } });

// Component to render different price presentations
const PricePresentationCore = ({ presentationType, priceProperties, placeHolderText = "", prices, pageId, className }: PricePresentationProps): JSX.Element | null => {
  const { locale, vatInc } = priceProperties;
  // tenant is kept for compatibility but no longer passed to internal components
  SetVersionOnWindow();
  const { isFeatureFlagEnabled } = useFeatureToggle();
  const showElev8WithTotalPriceOnConsumer = isFeatureFlagEnabled(SHOW_ELEV8_WITH_TOTALPRICE_ON_CONSUMER);

  const placeholderSkeletonText = <PriceLoading />;

  const renderPriceComponent = () => {
    switch (presentationType) {
      case PricePresentationType.PricePerUnit: {
        const priceInfo = getPricePerUnit(prices, priceProperties);
        return <PricePerUnit placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} />;
      }

      // V2 UPDATES: Add quantity from wrapper here - or should we migrate both to quantity starting at price?
      case PricePresentationType.QtyStartingAtPrice: {
        const priceInfo = getQuantityPrice(prices, priceProperties);
        return <QtyStartingAtPrice placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} classNameWrapper={className} />;
      }

      case PricePresentationType.QtyFromPrice: {
        const priceInfo = getQuantityFromPrice(prices, priceProperties);
        return <QtyFromPrice placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} classNameWrapper={className} />;
      }

      case PricePresentationType.RangePrice: {
        const priceInfo = getRangedPrice(prices, priceProperties);
        return <LegacyRangePrice placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} splitInMultipleLines />;
      }
      case PricePresentationType.PriceForQtyUnits: {
        const priceInfo = getPriceForQuantityUnits(prices, priceProperties);
        return <PriceForQtyUnits placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} />;
      }

      case PricePresentationType.PriceWithQtyAndUnit: {
        const priceInfo = getPriceWithQtyAndUnit(prices, priceProperties);
        return (
          <FlexBox flexDirection={"column"}>
            <PriceForQtyUnits
              placeHolderText={placeholderSkeletonText}
              pricingInfo={{
                ...priceInfo,
                hideVatMessage: true,
                hideShippingMessageInVatMessage: true,
              }}
            />
            {priceProperties.quantity > 1 && !priceProperties.hidePerUnitPrice && prices.unitDiscountedPrice && prices.unitListPrice ? (
              <Typography fontSize="small">
                <PricePerUnit placeHolderText={placeHolderText} pricingInfo={priceInfo} />
              </Typography>
            ) : (
              <Typography fontSize="small">
                <VatMessage
                  locale={locale}
                  vatInc={vatInc}
                />
              </Typography>
            )}
            {priceProperties.quantity > 1 && (<Typography fontSize="small">
              {!priceProperties.hideVolumeDiscount && <Savings prices={prices} priceProperties={priceProperties} />}
            </Typography>)}
            {!priceProperties.hideSetupFee && <Typography fontSize="small"><SetupFee priceProperties={priceProperties} /></Typography>}
          </FlexBox>
        );
      }

      // V2 UPDATES: Use this for Raw Price
      // V2 UPDATES: Update or copy this for RawAndPricePerUnit Wrapper (same thing with unit price on next line)
      case PricePresentationType.RawPrice: {
        const priceInfo = getPrice(prices, priceProperties);
        return <RawPrice placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} classNameWrapper={className} />;
      }

      case PricePresentationType.RawAndPricePerUnit: {
        const priceInfo = getRawAndUnitPrice(prices, priceProperties);
        return (
          <FlexBox flexDirection={"column"}>
            <RawPrice placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} classNameWrapper={className} />
            <PricePerUnit placeHolderText={placeHolderText} pricingInfo={priceInfo} />
          </FlexBox>
        );
      }

      case PricePresentationType.AsLowAsPricePerPiece: {
        const priceInfo = getAsLowAsPerPiecePrice(prices, priceProperties);
        return <AsLowAsPricePerPiece placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} classNameWrapper={className} />;
      }

      case PricePresentationType.PlusOrMinus: {
        const priceInfo = getPlusMinusPrice(prices, priceProperties);
        return <PlusOrMinus placeHolderText={placeholderSkeletonText} pricingInfo={priceInfo} classNameWrapper={className} />;
      }

      case PricePresentationType.SetupFee: {
        const setupFee = prices?.setupFeeDiscountedPrice ?? prices?.setupFeeListPrice;
        return <SetupFee priceProperties={priceProperties} setupFee={setupFee} />;
      }

      // Expansion Pack price presentation types
      case PricePresentationType.TotalPrice:
      case PricePresentationType.PriceInfo: {
        const priceInfoConfig = getCombinedPriceConfig(prices, priceProperties, PriceDisplayMode.TOTAL);
        return <PriceInfo placeholdertext={placeHolderText} priceInfoConfig={priceInfoConfig} alignVAT={pageId === PageId.PDC ? AlignVAT.RIGHT : AlignVAT.BOTTOM} />;
      }

      case PricePresentationType.UnitPriceInfo: {
        const priceInfoConfig = getCombinedPriceConfig(prices, priceProperties, PriceDisplayMode.UNIT);
        // remove showTotalPriceForEachVariant prop after successful launch of ELEV8 with total price on consumer
        return <PriceInfo placeholdertext={placeHolderText} priceInfoConfig={priceInfoConfig} showTotalPriceForEachVariant={showElev8WithTotalPriceOnConsumer} />;
      }

      case PricePresentationType.DetailedPriceInfo: {
        const priceInfoConfig = getDetailedPriceConfig(prices, priceProperties, PriceDisplayMode.TOTAL);
        return <PriceInfo placeholdertext={placeHolderText} priceInfoConfig={priceInfoConfig} />;
      }

      case PricePresentationType.FromPrice: {
        const fromPriceConfig = getFromPriceConfig(prices, priceProperties, PriceDisplayMode.TOTAL);
        return <FromPrice placeholdertext={placeHolderText} fromPriceConfig={fromPriceConfig} />;
      }

      case PricePresentationType.UnitFromPrice: {
        const fromPriceConfig = getFromPriceConfig(prices, priceProperties, PriceDisplayMode.UNIT);
        // remove showTotalPriceForEachVariant prop after successful launch of ELEV8 with total price on consumer
        return <FromPrice placeholdertext={placeHolderText} fromPriceConfig={fromPriceConfig} showTotalPriceForEachVariant={showElev8WithTotalPriceOnConsumer} />;
      }

      case PricePresentationType.UnitRangePrice: {
        const rangePriceConfig = getRangePriceConfig(prices, priceProperties, false);
        return (
          <>
            <RangePrice placeholdertext={placeHolderText} rangePriceConfig={rangePriceConfig} />
            {!priceProperties.hideMinimumOrderableQuantity && priceProperties.quantity && (
              <MinimumOrderableQuantity placeholdertext={placeHolderText} quantity={priceProperties.quantity} locale={locale as Locale} alwaysShowMOQInfo={priceProperties.alwaysShowMOQInfo} />
            )}
          </>
        );
      }

      //Tile view of ranged price
      case PricePresentationType.UnitRangePriceForTile: {
        const rangePriceConfig = getRangePriceConfig(prices, priceProperties, true);
        return (
          <>
            <RangePrice placeholdertext={placeHolderText} rangePriceConfig={rangePriceConfig} />
            {!priceProperties.hideMinimumOrderableQuantity && priceProperties.quantity && (
              <MinimumOrderableQuantity placeholdertext={placeHolderText} quantity={priceProperties.quantity} locale={locale as Locale} alwaysShowMOQInfo={priceProperties.alwaysShowMOQInfo} />
            )}
          </>
        );
      }

      case PricePresentationType.DifferentialPrice: {
        const differentialPriceConfig = getDifferentialPriceConfig(prices, priceProperties);
        return <DifferentialPrice placeholdertext={placeHolderText} differentialPriceConfig={differentialPriceConfig} />;
      }

      case PricePresentationType.DifferentialPriceForQuantity: {
        const differentialPriceForQuantityConfig = getDifferentialPriceForQuantityConfig(prices, priceProperties);
        return <DifferentialPriceForQuantity placeholdertext={placeHolderText} differentialPriceForQuantityConfig={differentialPriceForQuantityConfig} />;
      }

      case PricePresentationType.QtyForPrice: {
        const quantityForPriceConfig = getQuantityForPrice(prices, priceProperties);
        return <QuantityForPrice placeholdertext={placeHolderText} quantityForPriceConfig={quantityForPriceConfig} />;
      }
    }
  };

  const canRenderWithoutPrices = presentationType === PricePresentationType.SetupFee;

  if (!prices && !canRenderWithoutPrices) {
    return <PriceLoading />;
  }

  return renderPriceComponent();
};

const PricePresentation = (pricePresentationProps: PricePresentationProps) => {
  return (
    <CSRWrapper>
      <LocalizationsProvider localizations={getLocalizationsFor(pricePresentationProps.priceProperties.locale)}>
        <PriceErrorBoundary fallbackUi={<></>}>
          <Box aria-live="polite" aria-atomic="true" data-testid="price-presentation-test-id">
            <PricePresentationCore {...pricePresentationProps} />
          </Box>
        </PriceErrorBoundary>
      </LocalizationsProvider>
    </CSRWrapper>
  );
};

export { PricePresentation };
export type { PricePresentationProps };
export default PricePresentation;
